import React from 'react';
import {
  Edit,
  Users,
  Calendar,
  TrendingUp,
  Building2,
  Briefcase,
  Trash2,
  UserPlus
} from 'lucide-react';
import SharePositionButton from './SharePositionButton';

const PositionHeader = ({
  positionData,
  stats,
  onEditClick,
  showEditButton = true,
  onDeleteClick,
  showDeleteButton = false,
  onAddCandidateClick,
  showAddCandidateButton = false
}) => {
  if (!positionData) return null;

  // Calculer les statistiques si elles ne sont pas fournies
  const calculateLocalStats = () => {
    if (stats) return stats;

    if (!positionData) return { totalCandidates: 0, conversionRate: 0, activeSteps: 0 };

    const totalCandidates = positionData.candidates ? positionData.candidates.length : 0;
    const activeSteps = positionData.steps ? positionData.steps.filter(step => step.active).length : 0;

    // Calculer le taux de conversion
    let conversionRate = 0;
    if (totalCandidates > 0 && positionData.steps) {
      const okStep = positionData.steps.find(step => step.name && step.name.toLowerCase() === 'ok');
      if (okStep && positionData.candidates) {
        const okCandidates = positionData.candidates.filter(candidate =>
          candidate.stageId === okStep._id || candidate.stage === okStep.name
        ).length;
        conversionRate = parseFloat(((okCandidates / totalCandidates) * 100).toFixed(1));
      }
    }

    return {
      totalCandidates,
      conversionRate,
      activeSteps,
    };
  };

  const localStats = calculateLocalStats();

  return (
    <div className="bg-white rounded-lg border">
      <div className="px-4 sm:px-6 py-4">
        {/* Mobile and Tablet layout */}
        <div className="block lg:hidden">
          <div className="flex items-center justify-end mb-4 gap-2">
            <SharePositionButton positionId={positionData._id} positionTitle={positionData.title} />
            {showAddCandidateButton && (
              <button
                onClick={onAddCandidateClick}
                className="flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg transition-colors touch-manipulation min-h-[44px]"
              >
                <UserPlus className="h-5 w-5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden sm:inline text-sm">Ajouter</span>
              </button>
            )}
            {showEditButton && (
              <button
                onClick={onEditClick}
                className="flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg transition-colors touch-manipulation min-h-[44px]"
              >
                <Edit className="h-5 w-5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden sm:inline text-sm">Modifier</span>
              </button>
            )}
            {showDeleteButton && (
              <button
                onClick={onDeleteClick}
                className="flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded-lg transition-colors touch-manipulation min-h-[44px]"
              >
                <Trash2 className="h-5 w-5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden sm:inline text-sm">Supprimer</span>
              </button>
            )}
          </div>

          <div className="flex items-start space-x-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
              <Briefcase className="text-blue-600" size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 leading-tight break-words">{positionData.title}</h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium w-fit ${
                  positionData.open
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {positionData.open ? 'Ouvert' : 'Fermé'}
                </span>
                {positionData.partner && (
                  <div className="flex items-center text-gray-600 text-sm">
                    <Building2 className="mr-1 h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{positionData.partner.name}</span>
                    {positionData.partner.city && (
                      <span className="ml-2 text-gray-500 hidden sm:inline">• {positionData.partner.city}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile/Tablet Statistics - Responsive Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-2 min-w-0">
              <Users className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
              <div className="min-w-0">
                <div className="text-sm font-bold text-indigo-900">{localStats.totalCandidates}</div>
                <div className="text-xs text-indigo-700 truncate">candidats</div>
              </div>
            </div>

            <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-2 min-w-0">
              <div className="min-w-0">
                <div className="text-sm font-bold text-indigo-900">{localStats.activeSteps}</div>
                <div className="text-xs text-indigo-700 truncate">étapes</div>
              </div>
            </div>

            <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-2 min-w-0">
              <TrendingUp className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
              <div className="min-w-0">
                <div className="text-sm font-bold text-indigo-900">{localStats.conversionRate}%</div>
                <div className="text-xs text-indigo-700 truncate">conversion</div>
              </div>
            </div>

            <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-2 min-w-0 col-span-2 md:col-span-1">
              <Calendar className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
              <div className="min-w-0">
                <div className="text-xs text-indigo-700">créé le</div>
                <div className="text-xs font-bold text-indigo-900 truncate">
                  {new Date(positionData.createdAt).toLocaleDateString('fr-FR')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop layout */}
        <div className="hidden lg:flex items-start justify-between">
          <div className="flex items-center space-x-4 ">
            <div className="border-gray-300 pl-4">
              <div className="flex items-center space-x-3">
                <div>
                  <div className='flex items-center space-x-2'>
                    <div className="bg-gray-50 p-2 border rounded-lg">
                      <Briefcase className="text-blue-600" size={24} />
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-0">{positionData.title}</h1>
                  </div>

                  {/* Entreprise */}
                  {positionData.partner && (
                    <div className="flex items-center text-gray-600 mt-1 mb-2">
                      <Building2 className="mr-2 h-4 w-4" />
                      <span className="text-sm">{positionData.partner.name}</span>
                      {positionData.partner.city && (
                        <span className="ml-2 text-gray-500 text-sm">• {positionData.partner.city}</span>
                      )}
                    </div>
                  )}

                  {/* Desktop Statistics */}
                  <div className="flex flex-wrap gap-2 sm:gap-3 mt-2">
                    <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-1 min-w-0">
                      <Users className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                      <div className="flex items-center space-x-1">
                        <span className="text-xs font-bold text-indigo-700">{localStats.totalCandidates}</span>
                        <span className="text-xs text-indigo-700">candidats</span>
                      </div>
                    </div>

                    <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-1 min-w-0">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs font-bold text-indigo-700">{localStats.activeSteps}</span>
                        <span className="text-xs text-indigo-700">étapes actives</span>
                      </div>
                    </div>

                    <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-1 min-w-0">
                      <TrendingUp className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                      <div className="flex items-center space-x-1">
                        <span className="text-xs font-bold text-indigo-700">{localStats.conversionRate}%</span>
                        <span className="text-xs text-indigo-700">conversion</span>
                      </div>
                    </div>

                    <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-1 min-w-0">
                      <Calendar className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-indigo-700">créé le</span>
                        <span className="text-xs font-bold text-indigo-700">
                          {new Date(positionData.createdAt).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              positionData.open
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {positionData.open ? 'Ouvert' : 'Fermé'}
            </span>

            <SharePositionButton positionId={positionData._id} positionTitle={positionData.title} />

            {showAddCandidateButton && (
              <button
                onClick={onAddCandidateClick}
                className="flex items-center space-x-2 px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <UserPlus size={16} />
              </button>
            )}

            {showEditButton && (
              <button
                onClick={onEditClick}
                className="flex items-center space-x-2 px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Edit size={16} />
              </button>
            )}
            {showDeleteButton && (
              <button
                onClick={onDeleteClick}
                className="flex items-center space-x-2 px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionHeader;